import React from 'react'
import { useTranslation } from 'react-i18next'
import styled from 'styled-components'
import type {
  TooltipMetricExplanationProps,
  MetricCategory,
} from './TooltipMetricExplanation.types'

const Container = styled.div`
  font-size: 13px;
  line-height: 1.6;
  color: ${({ theme }) => theme.colors.text.primary};
`

const Title = styled.strong`
  font-weight: 600;
  font-size: 13px;
  color: ${({ theme }) => theme.colors.text.primary};
`

const Section = styled.div`
  margin-top: 12px;

  &:first-child {
    margin-top: 0;
  }
`

const SectionTitle = styled.strong`
  font-weight: 600;
  font-size: 13px;
  color: ${({ theme }) => theme.colors.text.primary};
  display: block;
  margin-bottom: 4px;
`

const CategoryList = styled.div`
  margin-top: 4px;
`

const CategoryItem = styled.div`
  margin-top: 4px;
  padding-left: 12px;

  &:first-child {
    margin-top: 0;
  }
`

const CategoryLabel = styled.strong`
  font-weight: 600;
  font-size: 13px;
  color: ${({ theme }) => theme.colors.text.primary};
`

const Warning = styled.div`
  display: block;
  margin-top: 12px;
  padding-top: 12px;
  border-top: 1px solid ${({ theme }) => theme.colors.border.light};
  color: ${({ theme }) => theme.colors.warning};
  font-size: 12px;
  line-height: 1.5;

  &::before {
    content: '⚠️';
    margin-right: 6px;
    font-size: 14px;
  }
`

const Source = styled.div`
  display: flex;
  align-items: center;
  gap: 6px;
  margin-top: 12px;
  padding: 8px 12px;
  background: ${({ theme }) => theme.colors.background.secondary};
  border-radius: 6px;
  border-left: 3px solid ${({ theme }) => theme.colors.primary};
  font-size: 11px;
  color: ${({ theme }) => theme.colors.text.secondary};
  line-height: 1.4;

  &::before {
    content: '📊';
    font-size: 12px;
    opacity: 0.8;
  }
`

const renderCategory = (category: MetricCategory) => {
  const label = category.range
    ? `${category.label} (${category.range})`
    : category.label
  return (
    <CategoryItem key={category.label}>
      • <CategoryLabel>{label}:</CategoryLabel> {category.description}
    </CategoryItem>
  )
}

export const TooltipMetricExplanation: React.FC<
  TooltipMetricExplanationProps
> = ({
  title,
  shortDescription,
  calculation,
  categories,
  importance,
  limitations,
  source,
}) => {
  const { t } = useTranslation('ui')

  return (
    <Container>
      <Section>
        <Title>{title}:</Title> {shortDescription}
      </Section>

      {calculation && (
        <Section>
          <SectionTitle>{t('tooltipMetric.howCalculated')}</SectionTitle>
          {calculation}
        </Section>
      )}

      {categories && categories.length > 0 && (
        <Section>
          <SectionTitle>
            {categories[0].range
              ? t('tooltipMetric.categories')
              : t('tooltipMetric.whatItMeans')}
          </SectionTitle>
          <CategoryList>{categories.map(renderCategory)}</CategoryList>
        </Section>
      )}

      {importance && (
        <Section>
          <SectionTitle>{t('tooltipMetric.whyItMatters')}</SectionTitle>
          {importance}
        </Section>
      )}

      {source && <Source>{source}</Source>}

      {limitations && <Warning>{limitations}</Warning>}
    </Container>
  )
}
